// Neon trim rail material — the pure-emissive light strips along the board
// top and base. The bloom pass does all the glowing; this shader only
// decides color (end-to-end home→away gradient) and intensity.
//
// FX hook: a board-hit pulse. `hitPos` (world) + `hitAge` (seconds) light
// up the rail segment near an impact and decay it; FxDirector writes these
// uniforms when the sim emits PuckBoardHit.

import { tuning } from "@arcade-hockey/sim";
import { clamp, mix, positionWorld, uniform } from "three/tsl";
import { Color, MeshBasicNodeMaterial, Vector3 } from "three/webgpu";
import type { BoardsStyle } from "../style/style-schema.js";

function buildUniforms(style: BoardsStyle) {
  return {
    leftColor: uniform(new Color(style.trim.leftColor)),
    rightColor: uniform(new Color(style.trim.rightColor)),
    intensity: uniform(style.trim.intensity),
    baseIntensity: uniform(style.trim.baseIntensity),
    pulseFalloff: uniform(style.trim.pulseFalloff),
    pulseBoost: uniform(style.trim.pulseBoost),
    /** FX: world position of the most recent board hit. */
    hitPos: uniform(new Vector3(0, 0, 0)),
    /** FX: seconds since the hit; large = no pulse. FxDirector advances this. */
    hitAge: uniform(1000),
  };
}

export type NeonTrimUniforms = ReturnType<typeof buildUniforms>;

export function applyNeonTrimStyle(u: NeonTrimUniforms, s: BoardsStyle): void {
  u.leftColor.value.set(s.trim.leftColor);
  u.rightColor.value.set(s.trim.rightColor);
  u.intensity.value = s.trim.intensity;
  u.baseIntensity.value = s.trim.baseIntensity;
  u.pulseFalloff.value = s.trim.pulseFalloff;
  u.pulseBoost.value = s.trim.pulseBoost;
}

export function createNeonTrimMaterial(style: BoardsStyle): {
  material: MeshBasicNodeMaterial;
  u: NeonTrimUniforms;
} {
  const u = buildUniforms(style);

  // Side-to-side gradient on world x, matching the reference render: the
  // left board runs cyan/blue, the right board magenta, blending through
  // the corners and ends.
  const sideT = clamp(positionWorld.x.div(tuning.rink.width).add(0.5), 0, 1);
  const railColor = mix(u.leftColor, u.rightColor, sideT);

  // Board-hit pulse: gaussian-ish falloff in distance, exponential in age.
  const dist = positionWorld.sub(u.hitPos).length();
  const spatial = dist.mul(u.pulseFalloff).mul(-1).exp();
  const temporal = u.hitAge.mul(-4).exp();
  const pulse = spatial.mul(temporal).mul(u.pulseBoost);

  // Top rail (uv.y stored 0.5 for all rail verts; intensity split uses
  // world height instead: the base rail hugs the ice, the top rail rides
  // the board lip).
  const isTop = clamp(positionWorld.y.mul(2), 0, 1);
  const railIntensity = mix(u.baseIntensity, u.intensity, isTop);

  const material = new MeshBasicNodeMaterial();
  material.colorNode = railColor.mul(railIntensity.add(pulse));
  return { material, u };
}
